import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MetaQueryBarComponent } from '@softapps/core/pages';
import { ListPageComponent, PageFooterComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  backNavigation,
  menuItems,
  queryParam,
  subtitle,
  title } from '@softline/application';
import { Store } from '@softline/core';
import {
  DefinitionStore,
  DynamicModule,
  SOFTLINE_FEATURE_DEFINITIONS } from '@softline/dynamic';
import { CardComponent, EmptyStateComponent, translator } from '@softline/ui-core';
import { SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST } from '../../arbeitsbericht.api';
import { SOFTLINE_COMPONENT_TIME_REPORT_ITEM } from '../../arbeitsbericht.shared';
import { TimeReportItemComponent } from '../../components/time-report-item/time-report-item.component';
import { timeReportQueryMeta } from '../../models/meta/time-report-query.meta';
import { timeReportMeta } from '../../models/meta/time-report.meta';
import { TimeReport } from '../../models/time-report.model';
import { TimeReportQuery } from '../../models/time-report.query';
import { TimeReportsService } from '../../services/time-reports.service';

import { FormatPipe } from '@softline/ui-core';
@Component({
  selector: 'soft-time-report-query-result',
  templateUrl: './query-result.component.html',
  styleUrls: ['./query-result.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DynamicModule,
    ListPageComponent,
    PageHeaderComponent,
    PageFooterComponent,
    MetaQueryBarComponent,
    TimeReportItemComponent,
    CardComponent,
    EmptyStateComponent,
    FormatPipe] })
export class QueryResultComponent {
  private readonly router = inject(Router);
  readonly timeReportsService = inject(TimeReportsService);
  private readonly store = inject(Store);

  private readonly t = translator();

  all = this.timeReportsService.value;
  loading = this.timeReportsService.isLoading;
  sums = this.timeReportsService.sum;
  readonly entryCount = computed(() => this.timeReportsService.value()?.length ?? 0);

  async navigateToQueryPage(): Promise<void> {
    await this.router.navigate(['/arbeitsbericht', 'query']);
  }

  objektQueryParam = queryParam('objekt');
  from = queryParam('from');
  to = queryParam('to');

  query = computed<TimeReportQuery | null>(() => {
    if (!this.objektQueryParam() && !this.from() && !this.to()) {
      return null;
    }
    if (this.from() && this.to()) {
      return {
        from: this.from() || '',
        to: this.to() || '' };
    }
    return null;
  });

  setQuery(query: TimeReportQuery | null): void {
    this.router.navigate(['/arbeitsbericht', 'result'], {
      queryParams: {
        from: query?.from || null,
        to: query?.to || null } });
  }

  definitions = this.store.signal(
    SOFTLINE_FEATURE_DEFINITIONS,
    DefinitionStore.getters.definitions
  );
  definition = computed(() => {
    const definitions = this.definitions();
    return definitions[SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST];
  });

  timeReportComponent = inject(SOFTLINE_COMPONENT_TIME_REPORT_ITEM);

  queryMeta = timeReportQueryMeta;
  meta = timeReportMeta;

  constructor() {
    backNavigation('/arbeitsbericht/query');
    title(this.t('#TIME_REPORT.TITLE'));
    subtitle(this.t('#TIME_REPORT.COMPONENTS.QUERY_RESULT.SUBTITLE'));
    menuItems(() => [
      {
        type: 'route',
        icon: 'fa-regular fa-plus',
        name: 'create-1',
        title: 'Neuer Eintrag',
        priority: 1,
        outlet: 'nav',
        routerLink: ['/arbeitsbericht', 'create'] },
      {
        name: 'arbeitsberichtOverview',
        type: 'route',
        priority: 2,
        outlet: 'nav',
        icon: 'fa-regular fa-calendar-check',
        title: '#TIME_REPORT.MENU.LIST',
        routerLink: ['/arbeitsbericht'] },
      {
        name: 'arbeitsberichtQuery',
        type: 'route',
        outlet: 'nav',
        priority: 3,
        icon: 'fa-regular fa-magnifying-glass',
        title: '#TIME_REPORT.MENU.QUERY',
        routerLink: ['/arbeitsbericht', 'query'] },
      {
        outlet: 'nav',
        type: 'route',
        icon: 'fa-regular fa-list',
        priority: 4,
        name: '#TIME_REPORT.MENU.QUERY_RESULT',
        title: '#TIME_REPORT.MENU.QUERY_RESULT',
        routerLink: ['/arbeitsbericht', 'result'] }]);
  }

  async onItemClick(item: TimeReport): Promise<void> {
    await this.router.navigate(['/arbeitsbericht', item.id, 'edit']);
  }
}
